import { kontiIzVnosa, vnosIzKontov, type NastavitveKazalnikov as Nastavitve } from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../lib/api';

/** Raven vrstice v besedilu. */
const RAVNI: Record<string, string> = {
  skupina: 'skupina izkaza',
  postavka: 'postavka izkaza',
  'postavka-cf': 'postavka denarnega toka',
  'skupina-cf': 'skupina denarnega toka',
  blok: 'blok bilance',
};

/**
 * Nastavitve kazalnikov za eno podjetje (docs/ARHITEKTURA.md, 6.3).
 *
 * Svoja stran, ločena od nastavitev razvrščanja. Tu so nastavitve, ki
 * veljajo samo za kazalnike: vklop posameznega kazalnika, konti razlike v
 * ceni, pregled, iz katerih vrstic sheme kazalniki berejo, in vloge vrstic,
 * ki jih je dodalo podjetje. Sprememba ne zahteva ponovne razvrstitve.
 */
export function NastavitveKazalnikov(): JSX.Element {
  const { id = '' } = useParams();
  const [nazivPodjetja, setNazivPodjetja] = useState('');
  const [nastavitve, setNastavitve] = useState<Nastavitve | null>(null);
  const [prodajna, setProdajna] = useState('');
  const [nabavna, setNabavna] = useState('');
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjeno, setShranjeno] = useState(false);
  const [shranjujem, setShranjujem] = useState(false);

  const osvezi = (): void => {
    api
      .nastavitveKazalnikov(id)
      .then((n) => {
        setNastavitve(n);
        setProdajna(vnosIzKontov(n.razlikaVCeni.prodajna));
        setNabavna(vnosIzKontov(n.razlikaVCeni.nabavna));
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Nastavitev ni bilo mogoče prebrati.');
      });
  };

  useEffect(osvezi, [id]);

  useEffect(() => {
    api
      .podjetja()
      .then((vsa) => {
        setNazivPodjetja(vsa.find((p) => p.id === id)?.naziv ?? '');
      })
      .catch(() => {
        /* naziv ni nujen za delovanje strani */
      });
  }, [id]);

  /** Posamezna sprememba (vloga, vklop): shrani in ponovno preberi stran. */
  const spremeni = (delo: Promise<unknown>, neuspeh: string): void => {
    setShranjujem(true);
    setNapaka(null);
    delo
      .then(osvezi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : neuspeh);
      })
      .finally(() => {
        setShranjujem(false);
      });
  };

  const shrani = (): void => {
    setShranjujem(true);
    setNapaka(null);
    setShranjeno(false);
    api
      .shraniKonteRazlikeVCeni(id, { prodajna: kontiIzVnosa(prodajna), nabavna: kontiIzVnosa(nabavna) })
      .then(() => {
        setShranjeno(true);
        osvezi();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Kontov ni bilo mogoče shraniti.');
      })
      .finally(() => {
        setShranjujem(false);
      });
  };

  const nazivVloge = new Map((nastavitve?.viri ?? []).map((v) => [v.vloga, v.naziv]));

  return (
    <main className="siroko">
      <div className="vsebina">
        <nav className="drobtine">
          <Link to="/">Podjetja</Link>
          <Link to={`/podjetje/${id}`}>{nazivPodjetja || 'Podjetje'}</Link>
        </nav>

        <h1>Nastavitve kazalnikov</h1>
        <p className="podnaslov">
          Velja samo za kazalnike tega podjetja. Sprememba ne zahteva ponovne razvrstitve knjižb.
        </p>

        {napaka !== null && <p className="napaka">{napaka}</p>}
        {nastavitve === null && napaka === null && <p>Berem nastavitve …</p>}

        {nastavitve !== null && (
          <>
            <section className="kartica">
              <h2>Kazalniki na strani Pregled</h2>
              <p className="namig">
                Izklopljen kazalnik se stranki ne prikaže. »Privzeto« pomeni, da podjetje sledi
                privzeti nastavitvi – tudi ko se ta spremeni; »Vklopljeno« in »Izklopljeno« veljata
                samo za to podjetje, ne glede na privzeto. Kazalnik brez osnove (na primer zaloge pri
                podjetju brez zalog) se ne prikaže, tudi če je vklopljen.
              </p>
              <table className="nastavitve">
                <thead>
                  <tr>
                    <th>Kazalnik</th>
                    <th>Nastavitev</th>
                    <th>Velja</th>
                  </tr>
                </thead>
                <tbody>
                  {nastavitve.kazalniki.map((k) => (
                    <tr key={k.koda}>
                      <td>
                        {k.naziv} <span className="drobno">· {k.nazivSkupine}</span>
                      </td>
                      <td>
                        <select
                          aria-label={`Nastavitev kazalnika ${k.naziv}`}
                          value={k.nastavitev}
                          disabled={shranjujem}
                          onChange={(e) => {
                            const izbira = e.target.value;
                            spremeni(
                              api.shraniVklopKazalnika(
                                id,
                                k.koda,
                                izbira === 'privzeto' ? null : izbira === 'vklopljeno',
                              ),
                              'Nastavitve ni bilo mogoče shraniti.',
                            );
                          }}
                        >
                          <option value="privzeto">
                            Privzeto ({k.privzeto ? 'vklopljen' : 'izklopljen'})
                          </option>
                          <option value="vklopljeno">Vklopljeno</option>
                          <option value="izklopljeno">Izklopljeno</option>
                        </select>
                      </td>
                      <td>{k.vklopljen ? 'prikazan' : 'skrit'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="kartica">
              <h2>Razlika v ceni</h2>
              <p className="namig">
                Konti, ki sestavljajo prodajno in nabavno vrednost. Vpiši šifre ali začetke šifer,
                ločene s podpičjem (npr. 7600; 7601). Isti konto ne more biti na obeh seznamih.
                Dokler ni vpisan noben konto, se blok razlike v ceni med kazalniki ne prikaže.
              </p>
              <label className="polje">
                Prodajna vrednost
                <input
                  value={prodajna}
                  onChange={(e) => {
                    setProdajna(e.target.value);
                    setShranjeno(false);
                  }}
                />
              </label>
              <label className="polje">
                Nabavna vrednost
                <input
                  value={nabavna}
                  onChange={(e) => {
                    setNabavna(e.target.value);
                    setShranjeno(false);
                  }}
                />
              </label>
              <p>
                <button type="button" className="gumb" onClick={shrani} disabled={shranjujem}>
                  {shranjujem ? 'Shranjujem …' : 'Shrani konte'}
                </button>
                {shranjeno && <span className="uspeh-besedilo"> Shranjeno.</span>}
              </p>
            </section>

            <section className="kartica">
              <h2>Iz katerih vrstic berejo kazalniki</h2>
              <p className="namig">
                Kazalniki vrstic ne iščejo po nazivu, ampak po vlogi, ki jo ima vrstica v shemi. Več
                vrstic z isto vlogo se sešteje. Vrstica, ki jo doda podjetje, vloge nima in v
                kazalniku ne nastopa, dokler ji vloge ne dodeliš spodaj.
              </p>
              <table className="nastavitve">
                <thead>
                  <tr>
                    <th>Vloga</th>
                    <th>Raven</th>
                    <th>Vrstice podjetja</th>
                  </tr>
                </thead>
                <tbody>
                  {nastavitve.viri.map((v) => (
                    <tr key={v.vloga}>
                      <td>{v.naziv}</td>
                      <td>{RAVNI[v.raven] ?? v.raven}</td>
                      <td>
                        {v.vrstice.length === 0 ? (
                          <span className="opozorilo">nobena – ta del v kazalnikih manjka</span>
                        ) : (
                          v.vrstice.join(' · ')
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="kartica">
              <h2>Vloge vrstic podjetja</h2>
              <p className="namig">
                Vrstice, ki ste jih dodali pri tem podjetju. Vloga pove, v katerem delu kazalnika
                vrstica nastopa; vrstica z isto vlogo kot standardna se ji pridruži. Na izbiro so samo
                vloge, ki sodijo na raven vrstice. Vloge standardne sheme so skupne vsem podjetjem.
              </p>
              {nastavitve.vrsticePodjetja.length === 0 ? (
                <p>To podjetje nima dodanih vrstic.</p>
              ) : (
                <table className="nastavitve">
                  <thead>
                    <tr>
                      <th>Vrstica</th>
                      <th>Raven</th>
                      <th>Vloga</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nastavitve.vrsticePodjetja.map((v) => (
                      <tr key={v.id}>
                        <td>
                          {v.naziv}
                          {v.nadrejena !== '' && <span className="drobno"> · {v.nadrejena}</span>}
                        </td>
                        <td>{RAVNI[v.raven] ?? v.raven}</td>
                        <td>
                          <select
                            aria-label={`Vloga vrstice ${v.naziv}`}
                            value={v.vloga ?? ''}
                            disabled={shranjujem}
                            onChange={(e) => {
                              spremeni(
                                api.shraniVlogoVrstice(id, {
                                  raven: v.raven,
                                  id: v.id,
                                  vloga: e.target.value === '' ? null : e.target.value,
                                }),
                                'Vloge ni bilo mogoče shraniti.',
                              );
                            }}
                          >
                            <option value="">brez vloge</option>
                            {v.mozneVloge.map((m) => (
                              <option key={m} value={m}>
                                {nazivVloge.get(m) ?? m}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
}
